/*
 * web通用请求基类 httpCreater
 * @Author: zhangxb
 * @since: 2021-06-05 17:47:51
 *
 * ======= Usage =======
 * 1. import httpCreater from '@yld/tool-http-creater';
 * 2. const http = httpCreater(httpConfig);
 * ======= Done!! ======
 *
 * httpConfig 配置项
 * {
 *   // 自定义请求配置
 *   http: <object> {
 *       // 接口超时时间（毫秒）
 *       timeout: <Int> 30000,
 *       // 请求时是否带cookie
 *       withCredentials: <Boolean> true,
 *       // http自定义请求头
 *       headers: <object> {}
 *   },
 *   // 请求前回调
 *   beforeRequest: <func> (opts) => opts
 *   // 请求成功回调
 *   onSuccess: <func> (res) => res
 *   // 请求失败统一回调
 *   onFailrue: <func> (res) => res,
 *   // 自定义java接口请求头header内容
 *   javaHeader: <object> {},
 *   // 自定义请求失败拦截器
 *   customOnFailrue: <func> (res) => res,
 * }
 *
 * 单http请求配置项
 * {
 *    // 是否静默请求（开启后本次请求所有行为都将静默处理，默认false）
 *    silent: <Boolean> false,
 *    // 是否上报异常（关闭后http异常将不再上报sentry，默认true）
 *    report: <Boolean> true,
 *    // 自定义http请求头内容（会覆盖http里headers的内容）
 *    headers: <object> {},
 *    // 自定义JAVA接口的请求头header内容（会覆盖javaHeader里面的内容）
 *    header: <object> {},
 * }
 */

import axios from 'axios'
import { isLocal, BASE_URL } from './host'
import _isPlainObject from 'lodash/isPlainObject'
import _isEmpty from 'lodash/isEmpty'
import _assign from 'lodash/assign'
import httpException from './http-exception'

// 取消请求的handle
// let cancelHandle;

// 默认配置
const defaultConfig = {
  http: {
    timeout: 30000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
    // todo: 重复请求取消请求中的前一条请求
    // cancelToken: new CancelToken(function executor(c) {
    //     // An executor function receives a cancel function as a parameter
    //     cancelHandle = c;
    // })
  },
  env: {
    // 是否本地开发环境
    isLocal: false,
    // 当前网关前缀地址
    gateway: ''
  },
  // 异常处理配置
  exception: {
    callback: null,
    interceptor: null
  }
}

const utils = {
  // 创建请求实例
  getInstance: config => {
    let createConfig = defaultConfig.http
    if (!_isEmpty(config.http)) {
      createConfig = _assign({}, defaultConfig.http, config.http)
    }
    return axios.create(createConfig)
  },
  // 初始化异常处理配置并缓存
  initException: httpConfig => {
    try {
      defaultConfig.exception.callback = httpConfig.onFailrue
      defaultConfig.exception.interceptor = httpConfig.customOnFailrue
      if (!httpException || typeof httpException !== 'function') {
        throw new Error('httpException加载异常')
      }
    } catch (e) {
      throw new Error('httpException加载异常')
    }
  },
  // 请求失败回调
  failureCallback: response => {
    return Promise.reject(httpException(response, defaultConfig.exception))
  },
  // 根据httpConfig初始化环境状态
  initEnv: () => {
    try {
      defaultConfig.env.isLocal = isLocal
      defaultConfig.env.gateway = BASE_URL.WG
    } catch (e) {
      throw new Error('http ENV环境缺失，请确认isLocal和gateway')
    }
  },
  // 校验env环境来判断是否需要补全网关前缀
  checkENV: url => {
    return !/http/.test(url) && !defaultConfig.env.isLocal
  }
}

// 拦截器
const interceptors = {
  // 请求前
  request: (opts, httpConfig) => {
    const { params = null, data = null } = opts
    // 保存原始参数
    opts.origin = {
      params,
      data,
      url: opts.url,
      method: opts.method,
      silent: !!opts.silent,
      report: !!opts.report
    }

    // 非缓存则追加时间戳
    // if (!opts.cache) {
    //     if(params) {
    //         opts.params = {};
    //     }
    //     opts.params['_'] = +new Date;
    // }

    // 执行自定义请求拦截器
    if (httpConfig.beforeRequest && typeof httpConfig.beforeRequest === 'function') {
      return httpConfig.beforeRequest(opts)
    }

    return opts
  },
  // http请求成功
  responseSuccess: (response, httpConfig) => {
    const origin = response.config.origin

    // 处理非通用数据格式
    if (!_isPlainObject(response.data) || !response.data.code) {
      return {
        code: 200,
        data: response.data,
        origin
      }
    }
    response.data.origin = origin
    const res = response.data
    if (res.code === 200 || res.code === 1 || res.code === 100) {
      // 执行自定义请求成功拦截器
      if (httpConfig.onSuccess && typeof httpConfig.onSuccess === 'function') {
        return httpConfig.onSuccess(res)
      }
      return res
    } else {
      return utils.failureCallback(res, httpConfig)
    }
  },
  // http请求失败
  responseError: (error, httpConfig) => {
    const response = error instanceof Error ? error.response || error : error
    let result
    if (response.data) {
      result = response.data
    } else {
      result = {
        code: response.status || -1,
        msg: response.statusText || '网络异常，请求超时。请稍后再试'
      }
    }
    result.origin = response.config.origin
    return utils.failureCallback(result, httpConfig)
  }
}

const httpCreater = (httpConfig = {}) => {
  // 初始化环境变量
  utils.initEnv()
  // 初始化异常处理
  utils.initException(httpConfig)
  // 生成请求实例
  const instance = utils.getInstance(httpConfig)
  // 注册请求前拦截器
  instance.interceptors.request.use(opts => {
    return interceptors.request(opts, httpConfig)
    // 定时器处理ES缓存同步慢的问题
    // return new Promise(resolve => {
    //     setTimeout(() => {
    //         resolve(interceptors.request(opts, httpConfig));
    //     }, 300)
    // });
  })
  // 注册请求后拦截器
  instance.interceptors.response.use(
    res => interceptors.responseSuccess(res, httpConfig),
    err => interceptors.responseError(err, httpConfig)
  )

  return {
    // 根据请求接口类型，处理data格式
    request(opts, isGet) {
      // 非本地环境如果接口地址为相对路径格式，用网关地址补全
      if (utils.checkENV(opts.url)) {
        opts.url = defaultConfig.env.gateway + opts.url
      }

      // 如果是java的接口且非GET请求方法，数据包裹一层Model
      if (!isGet) {
        let header = {
          // 'xClientHost': 'i-ebs-yj-gateway.sdlandsea.net'
          'xClientHost': 'fare.i-ebs.com'
        }
        if (httpConfig.javaHeader || opts.header) {
          header = _assign({}, httpConfig.javaHeader, opts.header)
        }

        opts.data = {
          header,
          model: opts.data
        }
      }

      return instance.request(opts)
    },

    // 根据api配置列表生成对应的api请求实体
    createApi(apiConfigMap) {
      const api = {}
      const that = this
      for (const key in apiConfigMap) {
        // eslint-disable-next-line no-prototype-builtins
        if (apiConfigMap.hasOwnProperty(key)) {
          const apiConfig = apiConfigMap[key]
          const method = apiConfig.method || 'post'
          api[key] = function(data, config) {
            if (that[method]) {
              return that[method](apiConfig.url, data, { ...apiConfig, ...config })
            }
          }
        }
      }
      return api
    },

    get(url, params, opts) {
      opts = opts || {}
      opts.method = 'get'
      opts.url = url
      if (!_isEmpty(params) || params) {
        opts.params = params
      }
      return this.request(opts, true)
    },

    delete(url, params, opts) {
      opts = opts || {}
      opts.method = 'delete'
      opts.url = url
      if (!_isEmpty(params) || params) {
        opts.params = params
      }
      return this.request(opts)
    },

    post(url, data, opts) {
      opts = opts || {}
      opts.method = 'post'
      opts.url = url
      if (data) {
        opts.data = data
      }
      return this.request(opts)
    },

    put(url, data, opts) {
      opts = opts || {}
      opts.method = 'put'
      opts.url = url
      if (data) {
        opts.data = data
      }
      return this.request(opts)
    },

    patch(url, data, opts) {
      opts = opts || {}
      opts.method = 'patch'
      opts.url = url
      if (data) {
        opts.data = data
      }
      return this.request(opts)
    },

    sendForm(url, data, { method = 'POST', target = '_blank' } = {}) {
      const form = document.createElement('form')
      form.action = url
      form.method = method
      form.target = target

      const setFiled = (key, val) => {
        var input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = val
        form.appendChild(input)
      }

      for (const key in data) {
        setFiled(key, data[key])
      }

      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    }
  }
}

export default httpCreater
